import React, { useEffect, useState } from 'react';
import { css, useTheme } from '@emotion/react';
import { BirdiTheme, breakpoints } from '@birdi/theme';
import { useMetaSelector } from '../../meta/reducer';
import { organisation as orgAPI, subscription as subscriptionAPI, stroot } from '@birdi/js-sdk';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import type { WidgetId } from '../tabs';

interface GraphData {
  id: WidgetId;
  label: string;
  used: number;
  total: number | string;
  color: string;
  unit?: string;
  centerText: string;
}

// Fixed order matching usage tab
const GRAPH_WIDGETS: Omit<GraphData, 'used' | 'total' | 'centerText'>[] = [
  {
    id: 'seats-usage',
    label: 'Seats usage',
    color: '#2287FA', // theme.ocean
  },
  {
    id: 'guest-access-usage',
    label: 'Guest access usage',
    color: '#FC7543', // theme.sunConure
  },
  {
    id: 'workspace-usage',
    label: 'Workspace usage',
    color: '#4AD294', // theme.flora
  },
  {
    id: 'processing-usage-graph',
    label: 'Processing usage',
    color: '#FFCA46', // theme.canary
    unit: ' GP',
  },
  {
    id: 'storage-usage',
    label: 'Storage usage',
    color: '#38385C', // theme.midnight
    unit: ' GB',
  },
];

export interface GraphWidgetsProps {
  selectedWidgetIds: WidgetId[];
}

export const GraphWidgets: React.FC<GraphWidgetsProps> = ({ selectedWidgetIds }) => {
  const meta = useMetaSelector();
  const theme = useTheme() as BirdiTheme;
  const [usageData, setUsageData] = useState<{
    seatsUsed: number;
    seatsTotal: number | string;
    guestsUsed: number;
    guestsTotal: number | string;
    workspacesUsed: number;
    workspacesTotal: number;
    processingUsed: number;
    processingTotal: number;
    storageUsed: number;
    storageTotal: number;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadAllData = async () => {
      try {
        const [
          storageRes,
          workspacesRes,
          seatsRes,
          processingRes,
          ...subscriptionResults
        ] = await Promise.all([
          orgAPI.getStorageInfo(stroot('faci1a')),
          orgAPI.getWorkspacesOptimized(stroot('wrki1a')),
          orgAPI.getSeatsCount(stroot('ussea1a')),
          orgAPI.getProcessingGPInfo(stroot('hi7ohd')),
          ...(!!meta.organisation.primaryOrgId
            ? []
            : [
                subscriptionAPI
                  .getCurrentPlan(stroot('phahc4'))
                  .catch(() => ({ body: null })),
              ]),
        ]);

        const storageUsed = parseFloat(
          parseFloat(
            String(storageRes.body?.totalUsedStorage || '0'),
          ).toFixed(1),
        );

        const workspaces = workspacesRes.body?.items;
        const workspacesUsed = Array.isArray(workspaces) ? workspaces.length : 0;

        const seatsUsage = {
          userSeatsUsed: Number(seatsRes.body?.userCount) || 0,
          guestSeatsUsed: Number(seatsRes.body?.guestCount) || 0,
        };

        const processingUsed = Number(processingRes.body?.processingGPUsage) || 0;

        const subscriptionRes = subscriptionResults[0];
        const currentPlan = subscriptionRes && 'body' in subscriptionRes && subscriptionRes.body
          ? subscriptionRes.body
          : null;

        setUsageData({
          seatsUsed: seatsUsage.userSeatsUsed,
          seatsTotal: currentPlan?.basePlan?.userLimitSeats || 0,
          guestsUsed: seatsUsage.guestSeatsUsed,
          guestsTotal: currentPlan?.basePlan?.guestsLimitSeats || 'Unlimited',
          workspacesUsed,
          workspacesTotal: currentPlan?.basePlan?.workspacesLimit || 0,
          processingUsed,
          processingTotal: currentPlan?.basePlan?.processingTermLimitGigapixels || 0,
          storageUsed,
          storageTotal: currentPlan?.basePlan?.storageLimitGigabytes || 0,
        });
      } catch (error) {
        console.error('Error fetching usage data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAllData();
  }, [meta.organisation.primaryOrgId]);

  if (isLoading || !usageData) {
    return (
      <div
        css={css`
          padding: 2em;
          text-align: center;
          color: ${theme.mono60};
        `}
      >
        Loading...
      </div>
    );
  }

  const calculatePercentage = (used: number, total: number | string): number => {
    if (total === 'Unlimited' || total === 0) return 0;
    return Math.min(100, Math.round((used / Number(total)) * 100));
  };

  const formatValue = (value: number, unit?: string): string => {
    if (unit === ' GB') {
      const formatted = value % 1 === 0 ? value.toString() : value.toFixed(2);
      return `${formatted}${unit}`;
    }
    if (unit === ' GP') {
      const formatted = value % 1 === 0 ? value.toString() : value.toFixed(2);
      return `${formatted}${unit}`;
    }
    return value.toString();
  };

  const getGraphData = (widgetId: WidgetId): GraphData | null => {
    const widgetMeta = GRAPH_WIDGETS.find((w) => w.id === widgetId);
    if (!widgetMeta) return null;

    let used: number;
    let total: number | string;
    let centerText: string;

    switch (widgetId) {
      case 'seats-usage':
        used = usageData.seatsUsed;
        total = usageData.seatsTotal;
        centerText = `Seats\n${used}/${total}`;
        break;
      case 'guest-access-usage':
        used = usageData.guestsUsed;
        total = usageData.guestsTotal;
        centerText = `Guests\n${used}/${total}`;
        break;
      case 'workspace-usage':
        used = usageData.workspacesUsed;
        total = usageData.workspacesTotal;
        centerText = `Workspace\n${used}/${total}`;
        break;
      case 'processing-usage-graph':
        used = usageData.processingUsed;
        total = usageData.processingTotal;
        centerText = `Processing credit\n${formatValue(used, ' GP')} /${formatValue(total, ' GP')}`;
        break;
      case 'storage-usage':
        used = usageData.storageUsed;
        total = usageData.storageTotal;
        centerText = `Storage\n${formatValue(used, ' GB')} / ${formatValue(total, ' GB')}`;
        break;
      default:
        return null;
    }

    return {
      ...widgetMeta,
      used,
      total,
      centerText,
    };
  };

  // Filter to only show selected graph widgets, maintaining the fixed order
  const visibleGraphs = GRAPH_WIDGETS.filter((widget) =>
    selectedWidgetIds.includes(widget.id)
  ).map((widget) => getGraphData(widget.id)).filter((graph): graph is GraphData => graph !== null);

  if (visibleGraphs.length === 0) {
    return null;
  }

  const renderDonutChart = (graph: GraphData) => {
    const percentage = calculatePercentage(graph.used, graph.total);
    const isUnlimited = graph.total === 'Unlimited';

    const data = isUnlimited
      ? [
          { name: 'used', value: 0, label: 'Used' },
          { name: 'remaining', value: 100, label: 'Available' },
        ]
      : [
          { name: 'used', value: percentage, label: 'Used' },
          { name: 'remaining', value: 100 - percentage, label: 'Available' },
        ];

    const filledColor = graph.color;

    const CustomTooltip = ({ active, payload }: any) => {
      if (active && payload && payload.length) {
        const entry = payload[0];
        const isUsed = entry.name === 'used';
        const label = isUsed
          ? `${percentage}% used`
          : `${100 - percentage}% available`;

        return (
          <div
            css={css`
              background: ${theme.mono0};
              border: 1px solid ${theme.mono30};
              border-radius: 6px;
              padding: 0.5em 0.75em;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
              font-size: 0.85rem;
              color: ${theme.midnight};
              z-index: 10000 !important;
              position: relative;
            `}
          >
            {label}
          </div>
        );
      }
      return null;
    };

    return (
      <div
        key={graph.id}
        css={css`
          background: ${theme.mono0};
          border-radius: 1rem;
          padding: 0.25em;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.15em;
          min-width: 0;
          &:hover {
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
          }
        `}
      >
        <h3
          css={css`
            margin: 0.5em 0.25em 0 0.25em;
            font-size: 0.9rem;
            font-weight: 600;
            color: ${theme.midnight};
            text-align: center;
          `}
        >
          {graph.label}
        </h3>
        <div
          css={css`
            position: relative;
            width: 150px;
            height: 150px;
            flex-shrink: 0;
            aspect-ratio: 1;
            @media (max-width: ${breakpoints.s}) {
              width: 110px;
              height: 110px;
            }
            svg {
              outline: none !important;
              * {
                outline: none !important;
              }
            }
            svg:focus,
            svg:focus-visible,
            svg *:focus,
            svg *:focus-visible {
              outline: none !important;
              border: none !important;
            }
          `}
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart
              style={{ outline: 'none' }}
            >
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius="65%"
                outerRadius="90%"
                startAngle={90}
                endAngle={-270}
                dataKey="value"
                stroke="none"
                style={{ outline: 'none' }}
              >
                {data.map((entry, index) => {
                  const isUsed = entry.name === 'used';
                  const opacity = isUsed ? 1 : 0.15;
                  return (
                    <Cell
                      key={`cell-${index}`}
                      fill={filledColor}
                      fillOpacity={opacity}
                      style={{ outline: 'none' }}
                    />
                  );
                })}
              </Pie>
              <Tooltip
                content={<CustomTooltip />}
                wrapperStyle={{ zIndex: 10000 }}
                offset={-10}
              />
            </PieChart>
          </ResponsiveContainer>
          <div
            css={css`
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              text-align: center;
              pointer-events: none;
            `}
          >
            <div
              css={css`
                font-size: 0.8rem;
                font-weight: 600;
                color: ${theme.midnight};
                line-height: 1.2;
                display: flex;
                flex-direction: column;
                align-items: center;
                text-align: center;
                @media (max-width: ${breakpoints.s}) {
                  font-size: 0.75rem;
                }
                @media (max-width: ${breakpoints.xs}) {
                  font-size: 0.65rem;
                }
              `}
            >
              {graph.centerText.split('\n').map((line, index) => (
                <div key={index}>{line}</div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      css={css`
        display: grid;
        padding: 0;
        /*
          auto-fit, not a fixed count: the column count has to follow AVAILABLE
          width, because the docked assistant panel takes 360px out of the page
          and a viewport media query can't see it. Fixed at 5 wide, a laptop
          with the panel open squeezed five donuts into the width of two.
        */
        grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
        gap: 0.5em;
        margin-bottom: 0.5em;
        min-width: 0;
      `}
    >
      {visibleGraphs.map(renderDonutChart)}
    </div>
  );
};

